// Main JavaScript functionality for the static website starter kit

class SiteManager {
  constructor() {
    this.init();
  }

  init() {
    this.setupMobileMenu();
    this.setupThemeToggle();
    this.setupLazyLoading();
    this.setupSmoothScrolling();
    this.setupFormValidation();
    this.setupSearchFunctionality();
    this.setupAnalytics();
    this.setupTypewriter();
    this.setupCounterAnimation();
    this.setupScrollAnimations();
    this.setupProductInteractions();
    this.setupCopyCodeButtons();
    this.setupVideoModal();
  }

  // Re-trigger CSS animations on scroll/section enter
  setupScrollAnimations() {
    if (!("IntersectionObserver" in window)) return;

    const animatedElements = document.querySelectorAll('[class*="animate-"]');

    const restartAnimation = (el) => {
      const prev = el.style.animation;
      el.style.animation = "none";
      // Force reflow
      void el.offsetWidth;
      el.style.animation = prev || "";
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            restartAnimation(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    animatedElements.forEach((el) => observer.observe(el));
  }

  // Product page interactions: size select and quantity controls
  setupProductInteractions() {
    const sizeGroup = document.querySelector('[data-size-group]');
    if (sizeGroup) {
      sizeGroup.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-size]');
        if (!btn) return;
        // Clear active state
        sizeGroup.querySelectorAll('[data-size]').forEach((b) => {
          b.classList.remove('border-2');
          b.classList.remove('font-semibold');
          b.classList.remove('text-[#D4383A]');
          b.classList.add('border');
          b.setAttribute('aria-pressed', 'false');
        });
        // Set active
        btn.classList.remove('border');
        btn.classList.add('border-2');
        btn.classList.add('font-semibold');
        btn.classList.add('text-[#D4383A]');
        btn.setAttribute('aria-pressed', 'true');
        // Emit a custom event for integration hooks
        document.dispatchEvent(new CustomEvent('product:sizeChanged', { detail: { size: btn.dataset.size } }));
      });
    }

    const qtyValue = document.querySelector('[data-qty-value]');
    const minusBtn = document.querySelector('[data-qty-minus]');
    const plusBtn = document.querySelector('[data-qty-plus]');
    if (qtyValue && minusBtn && plusBtn) {
      const setQty = (n) => {
        const val = Math.max(1, Math.min(99, n));
        qtyValue.textContent = String(val);
        document.dispatchEvent(new CustomEvent('product:quantityChanged', { detail: { quantity: val } }));
      };
      minusBtn.addEventListener('click', () => setQty(parseInt(qtyValue.textContent || '1', 10) - 1));
      plusBtn.addEventListener('click', () => setQty(parseInt(qtyValue.textContent || '1', 10) + 1));
    }
  }

  // Copy-to-clipboard for promo codes
  setupCopyCodeButtons() {
    const buttons = document.querySelectorAll('[data-copy-code]');
    if (!buttons.length) return;

    buttons.forEach((btn) => {
      btn.addEventListener('click', async () => {
        const code = btn.getAttribute('data-copy-code') || '';
        const statusElId = btn.getAttribute('aria-describedby');
        const statusEl = statusElId ? document.getElementById(statusElId) : null;

        const success = await window.siteUtils.copyToClipboard(code);
        if (statusEl) {
          statusEl.textContent = success ? 'Copied!' : 'Copy failed';
        }

        const originalText = btn.textContent;
        btn.textContent = success ? 'Copied!' : 'Copy failed';
        btn.disabled = true;
        setTimeout(() => {
          btn.textContent = originalText;
          btn.disabled = false;
          if (statusEl) statusEl.textContent = '';
        }, 1500);
      });
    });
  }

  // Video modal for How to use section
  setupVideoModal() {
    const openBtn = document.querySelector('[data-video-open]');
    const modal = document.querySelector('[data-video-modal]');
    const closeEls = document.querySelectorAll('[data-video-close]');
    const videoEl = modal ? modal.querySelector('[data-video-el]') : null;
    const chapterButtons = document.querySelectorAll('[data-seek]');

    if (!openBtn || !modal || !videoEl) return;

    const videoSrc = openBtn.getAttribute('data-video-src');

    const open = () => {
      modal.classList.remove('hidden');
      if (videoEl.src !== videoSrc) {
        videoEl.src = videoSrc;
      }
      videoEl.play().catch(() => {});
      document.body.classList.add('overflow-hidden');
    };

    const close = () => {
      modal.classList.add('hidden');
      try { videoEl.pause(); } catch (_) {}
      document.body.classList.remove('overflow-hidden');
    };

    openBtn.addEventListener('click', open);
    closeEls.forEach((el) => el.addEventListener('click', close));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !modal.classList.contains('hidden')) close();
    });

    // Chapter seek buttons
    chapterButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const t = parseFloat(btn.getAttribute('data-seek') || '0');
        if (modal.classList.contains('hidden')) open();
        if (!Number.isNaN(t)) {
          videoEl.currentTime = t;
          videoEl.play().catch(() => {});
        }
      });
    });
  }

  // Mobile menu functionality
  setupMobileMenu() {
    const mobileMenuButton = document.getElementById("mobile-menu-button");
    const mobileMenu = document.getElementById("mobile-menu");

    if (mobileMenuButton && mobileMenu) {
      mobileMenuButton.addEventListener("click", () => {
        const isHidden = mobileMenu.classList.contains("hidden");

        if (isHidden) {
          mobileMenu.classList.remove("hidden");
          mobileMenuButton.setAttribute("aria-expanded", "true");
        } else {
          mobileMenu.classList.add("hidden");
          mobileMenuButton.setAttribute("aria-expanded", "false");
        }
      });

      // Close mobile menu when clicking outside
      document.addEventListener("click", (e) => {
        if (
          !mobileMenuButton.contains(e.target) &&
          !mobileMenu.contains(e.target)
        ) {
          mobileMenu.classList.add("hidden");
          mobileMenuButton.setAttribute("aria-expanded", "false");
        }
      });

      // Close mobile menu when pressing Escape
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && !mobileMenu.classList.contains("hidden")) {
          mobileMenu.classList.add("hidden");
          mobileMenuButton.setAttribute("aria-expanded", "false");
        }
      });
    }
  }

  // Theme toggle functionality
  setupThemeToggle() {
    const themeToggle = document.getElementById("theme-toggle");
    const darkIcon = document.getElementById("theme-toggle-dark-icon");
    const lightIcon = document.getElementById("theme-toggle-light-icon");

    if (themeToggle && darkIcon && lightIcon) {
      // Get theme from localStorage or system preference
      const theme =
        localStorage.getItem("theme") ||
        (window.matchMedia("(prefers-color-scheme: dark)").matches
          ? "dark"
          : "light");

      this.setTheme(theme, darkIcon, lightIcon);

      themeToggle.addEventListener("click", () => {
        const currentTheme = document.documentElement.classList.contains("dark")
          ? "dark"
          : "light";
        const newTheme = currentTheme === "dark" ? "light" : "dark";
        this.setTheme(newTheme, darkIcon, lightIcon);
        localStorage.setItem("theme", newTheme);
      });
    }
  }

  setTheme(theme, darkIcon, lightIcon) {
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
      darkIcon.classList.add("hidden");
      lightIcon.classList.remove("hidden");
    } else {
      document.documentElement.classList.remove("dark");
      darkIcon.classList.remove("hidden");
      lightIcon.classList.add("hidden");
    }
  }

  // Lazy loading for images
  setupLazyLoading() {
    if ("IntersectionObserver" in window) {
      const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target;
            img.src = img.dataset.src;
            img.classList.remove("lazy");
            observer.unobserve(img);
          }
        });
      });

      const lazyImages = document.querySelectorAll("img[data-src]");
      lazyImages.forEach((img) => imageObserver.observe(img));
    }
  }

  // Smooth scrolling for anchor links
  setupSmoothScrolling() {
    document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
      anchor.addEventListener("click", function (e) {
        e.preventDefault();
        const target = document.querySelector(this.getAttribute("href"));
        if (target) {
          target.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      });
    });
  }

  // Basic form validation
  setupFormValidation() {
    const forms = document.querySelectorAll("form[data-validate]");

    forms.forEach((form) => {
      form.addEventListener("submit", (e) => {
        let isValid = true;
        const requiredFields = form.querySelectorAll("[required]");

        requiredFields.forEach((field) => {
          if (!field.value.trim()) {
            this.showFieldError(field, "This field is required");
            isValid = false;
          } else {
            this.clearFieldError(field);
          }
        });

        // Email validation
        const emailFields = form.querySelectorAll('input[type="email"]');
        emailFields.forEach((field) => {
          if (field.value && !this.isValidEmail(field.value)) {
            this.showFieldError(field, "Please enter a valid email address");
            isValid = false;
          }
        });

        if (!isValid) {
          e.preventDefault();
        }
      });
    });
  }

  showFieldError(field, message) {
    this.clearFieldError(field);
    field.classList.add("border-red-500");
    const errorDiv = document.createElement("div");
    errorDiv.className = "form-error";
    errorDiv.textContent = message;
    field.parentNode.appendChild(errorDiv);
  }

  clearFieldError(field) {
    field.classList.remove("border-red-500");
    const existingError = field.parentNode.querySelector(".form-error");
    if (existingError) {
      existingError.remove();
    }
  }

  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  // Simple search functionality
  setupSearchFunctionality() {
    const searchInput = document.getElementById("search-input");
    const searchResults = document.getElementById("search-results");

    if (searchInput && searchResults) {
      let searchTimeout;

      searchInput.addEventListener("input", (e) => {
        clearTimeout(searchTimeout);
        searchTimeout = setTimeout(() => {
          this.performSearch(e.target.value, searchResults);
        }, 300);
      });
    }
  }

  async performSearch(query, resultsContainer) {
    if (query.length < 2) {
      resultsContainer.innerHTML = "";
      return;
    }

    try {
      // This would typically fetch from a search API or static search index
      // For now, we'll just show a placeholder
      resultsContainer.innerHTML = `
        <div class="p-4 text-gray-600">
          Searching for "${query}"...
        </div>
      `;
    } catch (error) {
      console.error("Search error:", error);
      resultsContainer.innerHTML = `
        <div class="p-4 text-red-600">
          Search failed. Please try again.
        </div>
      `;
    }
  }

  // Analytics tracking
  setupAnalytics() {
    // Track page views
    this.trackPageView();

    // Track outbound links
    document.querySelectorAll('a[href^="http"]').forEach((link) => {
      link.addEventListener("click", (e) => {
        const url = e.target.href;
        if (!url.includes(window.location.hostname)) {
          this.trackEvent("outbound_link", {
            url: url,
            text: e.target.textContent,
          });
        }
      });
    });

    // Track form submissions
    document.querySelectorAll("form").forEach((form) => {
      form.addEventListener("submit", () => {
        this.trackEvent("form_submit", {
          form_id: form.id || "unknown",
          form_action: form.action || window.location.href,
        });
      });
    });
  }

  trackPageView() {
    if (typeof gtag !== "undefined") {
      gtag("config", "GA_TRACKING_ID", {
        page_title: document.title,
        page_location: window.location.href,
      });
    }
  }

  trackEvent(eventName, parameters = {}) {
    if (typeof gtag !== "undefined") {
      gtag("event", eventName, parameters);
    }

    // For Plausible analytics
    if (typeof plausible !== "undefined") {
      plausible(eventName, { props: parameters });
    }
  }

  // Typewriter effect
  setupTypewriter() {
    const typewriterText = document.querySelector(".typewriter-text");

    if (typewriterText) {
      this.typewriterEffect(typewriterText, 50);
    }
  }

  typewriterEffect(element, speed = 50, callback = null) {
    const text = element.getAttribute("data-text");
    let index = 0;

    element.textContent = "";

    const type = () => {
      if (index < text.length) {
        element.textContent += text.charAt(index);
        index++;
        setTimeout(type, speed);
      } else {
        if (callback) callback();
      }
    };

    type();
  }

  // Counter animation
  setupCounterAnimation() {
    const counters = document.querySelectorAll(".count-number");

    counters.forEach((counter) => {
      const target = parseInt(counter.getAttribute("data-target"));
      const direction = (
        counter.getAttribute("data-direction") || "up"
      ).toLowerCase();
      const customStart = counter.hasAttribute("data-start")
        ? parseInt(counter.getAttribute("data-start"))
        : null;
      const suffix = counter.hasAttribute("data-suffix")
        ? counter.getAttribute("data-suffix")
        : target === 100
          ? "%"
          : target === 7
            ? ""
            : "+";

      const duration = 2000; // 2 seconds
      const frameTime = 16; // ~60fps
      let current;
      let step;

      if (direction === "down") {
        current = Number.isFinite(customStart) ? customStart : target * 2;
        step = (current - target) / (duration / frameTime);
      } else {
        current = 0;
        step = target / (duration / frameTime);
      }

      const updateCounter = () => {
        if (direction === "down") {
          if (current > target) {
            current -= step;
            if (current < target) current = target;
            counter.textContent = `${Math.floor(current)}${suffix}`;
            requestAnimationFrame(updateCounter);
          } else {
            counter.textContent = `${target}${suffix}`;
          }
        } else {
          if (current < target) {
            current += step;
            if (current > target) current = target;
            counter.textContent = `${Math.floor(current)}${suffix}`;
            requestAnimationFrame(updateCounter);
          } else {
            counter.textContent = `${target}${suffix}`;
          }
        }
      };

      // Start animation when element is visible
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            updateCounter();
            observer.unobserve(entry.target);
          }
        });
      });

      observer.observe(counter);
    });
  }
}

// Utility functions
const utils = {
  // Debounce function
  debounce(func, wait, immediate) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        timeout = null;
        if (!immediate) func(...args);
      };
      const callNow = immediate && !timeout;
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
      if (callNow) func(...args);
    };
  },

  // Throttle function
  throttle(func, limit) {
    let inThrottle;
    return function (...args) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  },

  // Format date
  formatDate(date, options = {}) {
    const defaultOptions = {
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return new Date(date).toLocaleDateString("en-US", {
      ...defaultOptions,
      ...options,
    });
  },

  // Copy to clipboard
  async copyToClipboard(text) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      console.error("Failed to copy: ", err);
      return false;
    }
  },

  // Get reading time
  getReadingTime(text) {
    const wordsPerMinute = 200;
    const wordCount = text.split(/\s+/).length;
    const minutes = Math.ceil(wordCount / wordsPerMinute);
    return minutes;
  },
};

// Initialize the site when DOM is loaded
document.addEventListener("DOMContentLoaded", () => {
  new SiteManager();
});

// Make utils available globally
window.siteUtils = utils;
